import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Compass, Library as LibraryIcon, Flame, Clock, Download, Quote } from "lucide-react";
import { toast } from "sonner";

function SortPill({ active, label, icon, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-blue-600 border-blue-500 text-white"
          : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

export default function MarketplacePage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [libs, setLibs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("popular");
  const [importingId, setImportingId] = useState(null);

  const load = async (s = sort) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/public/marketplace/caption-libraries`, {
        params: { sort: s, limit: 24 },
      });
      setLibs(data.libraries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load marketplace");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(sort); /* eslint-disable-next-line */ }, [sort]);

  const importLib = async (lib) => {
    if (!user) {
      nav(`/login?redirect=${encodeURIComponent(`/share/library/${lib.share_id}`)}`);
      return;
    }
    setImportingId(lib.id);
    try {
      const { data } = await api.post(`/public/caption-libraries/${lib.share_id}/import`);
      toast.success(`Added “${data.name}” to your libraries`);
      // Optimistically bump the local count
      setLibs((arr) =>
        arr.map((l) => (l.id === lib.id ? { ...l, import_count: (l.import_count || 0) + 1 } : l))
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImportingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <header className="border-b border-zinc-800 px-6 h-14 flex items-center justify-between">
        <Link to="/" data-testid="marketplace-brand" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-md grid place-items-center font-display font-bold text-white text-sm">
            AJ
          </div>
          <div className="font-display text-base leading-none">Ad Journal Generator</div>
        </Link>
        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/dashboard">
              <Button variant="ghost" className="text-zinc-200 hover:text-white hover:bg-zinc-800">
                My dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login">
              <Button data-testid="marketplace-signin" className="bg-blue-600 hover:bg-blue-500">
                Sign in
              </Button>
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-10 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-1">
              <Compass className="w-3 h-3" /> Explore
            </div>
            <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight">
              Caption libraries,
              <br />
              <span className="text-blue-400">used by real journals.</span>
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl text-sm">
              Public caption packs you can drop into your own journals in one click.
              Start a tournament book in seconds — your sponsors’ empty captions get filled
              with crowd-tested messages.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-1">
            <SortPill
              testId="marketplace-sort-popular"
              active={sort === "popular"}
              label="Most copied"
              icon={<Flame className="w-3.5 h-3.5" />}
              onClick={() => setSort("popular")}
            />
            <SortPill
              testId="marketplace-sort-recent"
              active={sort === "recent"}
              label="Recent"
              icon={<Clock className="w-3.5 h-3.5" />}
              onClick={() => setSort("recent")}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-zinc-500">Loading marketplace…</div>
        ) : libs.length === 0 ? (
          <div className="border border-dashed border-zinc-800 rounded-md p-12 text-center">
            <LibraryIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 mb-1">No public libraries yet.</p>
            <p className="text-zinc-500 text-sm mb-5">
              Be the first — create a library and toggle sharing on to feature it here.
            </p>
            {user ? (
              <Link to="/caption-libraries">
                <Button className="bg-blue-600 hover:bg-blue-500">Create a library</Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-blue-600 hover:bg-blue-500">Sign in to publish</Button>
              </Link>
            )}
          </div>
        ) : (
          <div data-testid="marketplace-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {libs.map((lib, i) => {
              const isPopular = sort === "popular" && i < 3 && (lib.import_count || 0) > 0;
              const sampleTemplate = (lib.templates || [])[0] || "";
              return (
                <article
                  key={lib.id}
                  data-testid={`marketplace-card-${lib.id}`}
                  className="bg-zinc-900 border border-zinc-800 rounded-md p-5 hover:border-zinc-700 transition-colors flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <Link
                        to={`/share/library/${lib.share_id}`}
                        data-testid={`marketplace-open-${lib.id}`}
                        className="font-display font-semibold truncate hover:text-blue-300 transition-colors"
                      >
                        {lib.name}
                      </Link>
                      {lib.description && (
                        <div className="text-xs text-zinc-500 truncate">{lib.description}</div>
                      )}
                    </div>
                    {isPopular && (
                      <span
                        data-testid={`marketplace-popular-badge-${lib.id}`}
                        className="shrink-0 inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-500/30"
                      >
                        <Flame className="w-3 h-3" /> Top
                      </span>
                    )}
                  </div>

                  <div className="text-[11px] text-zinc-500 mb-3 flex items-center gap-3">
                    <span>{lib.templates?.length || 0} templates</span>
                    <span>·</span>
                    <span>{Object.keys(lib.variables || {}).length} variables</span>
                  </div>

                  {sampleTemplate && (
                    <blockquote className="bg-zinc-950 border-l-2 border-blue-500/60 pl-3 pr-2 py-2 text-xs text-zinc-300 mb-4 flex-1 line-clamp-3 relative">
                      <Quote className="w-3 h-3 text-zinc-700 absolute top-1 right-1.5" />
                      {sampleTemplate}
                    </blockquote>
                  )}

                  <div className="flex items-center justify-between gap-2">
                    {(lib.import_count || 0) > 0 ? (
                      <div
                        data-testid={`marketplace-count-${lib.id}`}
                        className="text-[11px] text-zinc-400 inline-flex items-center gap-1"
                      >
                        <Download className="w-3 h-3 text-emerald-400" />
                        <span className="text-zinc-200 font-semibold">{lib.import_count}</span>
                        <span>copied this</span>
                      </div>
                    ) : (
                      <div className="text-[11px] text-zinc-600">Be the first to copy</div>
                    )}
                    <Button
                      size="sm"
                      data-testid={`marketplace-import-${lib.id}`}
                      onClick={() => importLib(lib)}
                      disabled={importingId === lib.id}
                      className="h-7 text-xs bg-blue-600 hover:bg-blue-500 shrink-0"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      {importingId === lib.id ? "Adding…" : (user ? "Copy" : "Sign in to copy")}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <p className="text-xs text-zinc-600 text-center mt-12">
          Want yours featured here? Toggle <strong className="text-zinc-400">Share</strong> on any
          caption library in your account — every public library appears in this list automatically.
        </p>
      </main>
    </div>
  );
}
